// Catálogo dos tipos de passo — Fase 10.
//
// É o que a paleta lista, o que o painel de propriedades desenha e o que a
// validação consulta. Uma tabela só, em vez de três `switch` espalhados: com
// `switch`, acrescentar um tipo de passo exige lembrar dos três lugares, e o
// esquecido não dá erro — ele só some da tela.
//
// As CHAVES DE `config` NÃO SÃO LIVRES. Cada uma é lida por nome em
// `_shared/fluxo/avancar.ts`; renomear `texto` para `mensagem` aqui faria o
// editor gravar um campo que o motor não lê, e o fluxo pararia em produção com
// "o passo de enviar mensagem ficou sem texto" — apontando para o lugar errado.

import type { Operador, TipoNo } from './tipos.ts';

/** Como o passo se liga ao seguinte. */
export type FormaDeSaida =
  /** Uma saída só. */
  | 'unica'
  /** Duas: verdadeiro e falso. */
  | 'condicional'
  /** Nenhuma: o caminho acaba aqui. */
  | 'nenhuma';

export type TipoCampo = 'texto' | 'texto_longo' | 'numero' | 'tag' | 'usuario' | 'escolha' | 'condicao';

export interface CampoConfig {
  /** Chave dentro de `config`. Lida por nome pelo motor — ver o aviso acima. */
  chave: string;
  rotulo: string;
  tipo: TipoCampo;
  obrigatorio: boolean;
  /** Dica curta sob o campo. */
  ajuda?: string;
  /** Só para `escolha`. */
  opcoes?: { valor: string; rotulo: string }[];
  padrao?: string | number;
}

export interface TipoDePasso {
  tipo: TipoNo;
  rotulo: string;
  /** Uma frase, em português de quem atende — não de quem programa. */
  resumo: string;
  saida: FormaDeSaida;
  /** Classe Tailwind da faixa colorida do nó no canvas. */
  cor: string;
  campos: CampoConfig[];
  /** Passo que fala com o cliente. A UI destaca: é o que tem consequência. */
  falaComCliente?: boolean;
}

export const OPERADORES: { valor: Operador; rotulo: string; precisaValor: boolean }[] = [
  { valor: 'igual', rotulo: 'é igual a', precisaValor: true },
  { valor: 'diferente', rotulo: 'é diferente de', precisaValor: true },
  { valor: 'contem', rotulo: 'contém', precisaValor: true },
  { valor: 'nao_contem', rotulo: 'não contém', precisaValor: true },
  { valor: 'comeca_com', rotulo: 'começa com', precisaValor: true },
  { valor: 'vazio', rotulo: 'está vazio', precisaValor: false },
  { valor: 'nao_vazio', rotulo: 'não está vazio', precisaValor: false },
  { valor: 'maior_que', rotulo: 'é maior que', precisaValor: true },
  { valor: 'menor_que', rotulo: 'é menor que', precisaValor: true },
];

/**
 * Os campos que a mensagem do fluxo pode interpolar.
 *
 * Cópia da lista fechada de `_shared/fluxo/interpolar.ts`. Telefone e e-mail
 * não estão lá e não podem entrar aqui: o editor é operado por quem atende, e
 * um `{{contato.telefone}}` sugerido pela tela viraria o telefone do cliente
 * dentro da mensagem automática enviada a ele.
 */
export const CAMPOS_INTERPOLAVEIS = [
  'contato.nome',
  'contato.primeiro_nome',
  'conversa.assunto',
  'conversa.protocolo',
  'ticket.protocolo',
  'empresa.nome',
  'atendente.nome',
] as const;

export const CATALOGO: TipoDePasso[] = [
  {
    tipo: 'enviar_mensagem',
    rotulo: 'Enviar mensagem',
    resumo: 'Escreve para o cliente no canal da conversa.',
    saida: 'unica',
    cor: 'bg-primary',
    falaComCliente: true,
    campos: [
      {
        chave: 'texto',
        rotulo: 'Texto',
        tipo: 'texto_longo',
        obrigatorio: true,
        ajuda: 'Use {{contato.primeiro_nome}} para personalizar. Campo fora da lista sai vazio.',
      },
    ],
  },
  {
    tipo: 'aguardar_resposta',
    rotulo: 'Aguardar resposta',
    resumo: 'Para aqui até o cliente responder. A resposta fica disponível para a condição.',
    saida: 'unica',
    cor: 'bg-sky-500',
    campos: [],
  },
  {
    tipo: 'condicao',
    rotulo: 'Condição',
    resumo: 'Divide o caminho em dois: quando dá certo e quando não dá.',
    saida: 'condicional',
    cor: 'bg-amber-500',
    campos: [
      {
        chave: 'condicao',
        rotulo: 'Regra',
        tipo: 'condicao',
        obrigatorio: true,
        ajuda: 'O campo mais usado é "resposta" — o que o cliente acabou de escrever.',
      },
    ],
  },
  {
    tipo: 'espera',
    rotulo: 'Esperar',
    resumo: 'Segura o fluxo por um tempo antes de seguir.',
    saida: 'unica',
    cor: 'bg-violet-500',
    campos: [
      {
        chave: 'segundos',
        rotulo: 'Tempo (segundos)',
        tipo: 'numero',
        obrigatorio: true,
        padrao: 60,
        ajuda: 'A espera é enfileirada com atraso, não é um timer preso. O teto por passo é 1 hora.',
      },
    ],
  },
  {
    tipo: 'aplicar_tag',
    rotulo: 'Aplicar etiqueta',
    resumo: 'Marca a conversa com uma etiqueta.',
    saida: 'unica',
    cor: 'bg-teal-500',
    campos: [{ chave: 'tagId', rotulo: 'Etiqueta', tipo: 'tag', obrigatorio: true }],
  },
  {
    tipo: 'atribuir_responsavel',
    rotulo: 'Atribuir responsável',
    resumo: 'Coloca a conversa na fila de alguém.',
    saida: 'unica',
    cor: 'bg-indigo-500',
    campos: [{ chave: 'userId', rotulo: 'Pessoa', tipo: 'usuario', obrigatorio: true }],
  },
  {
    tipo: 'abrir_chamado',
    rotulo: 'Abrir chamado',
    resumo: 'Gera protocolo e coloca a conversa no fluxo de chamados.',
    saida: 'unica',
    cor: 'bg-rose-500',
    campos: [
      { chave: 'assunto', rotulo: 'Assunto', tipo: 'texto', obrigatorio: true },
      {
        chave: 'prioridade',
        rotulo: 'Prioridade',
        tipo: 'escolha',
        obrigatorio: false,
        padrao: 'normal',
        opcoes: [
          { valor: 'baixa', rotulo: 'Baixa' },
          { valor: 'normal', rotulo: 'Normal' },
          { valor: 'alta', rotulo: 'Alta' },
          { valor: 'urgente', rotulo: 'Urgente' },
        ],
      },
    ],
  },
  {
    tipo: 'chamar_webhook',
    rotulo: 'Chamar webhook',
    resumo: 'Avisa um sistema de fora (n8n, por exemplo).',
    saida: 'unica',
    cor: 'bg-slate-500',
    campos: [
      {
        chave: 'url',
        rotulo: 'Endereço',
        tipo: 'texto',
        obrigatorio: true,
        ajuda: 'Precisa ser https e endereço público. Rede interna é recusada pelo worker.',
      },
    ],
  },
  {
    tipo: 'fim',
    rotulo: 'Fim',
    resumo: 'Encerra o fluxo nesta conversa.',
    saida: 'nenhuma',
    cor: 'bg-gray-400',
    campos: [],
  },
];

const POR_TIPO = new Map(CATALOGO.map((t) => [t.tipo, t]));

/** Devolve undefined para tipo desconhecido — quem chama decide o que fazer.
 *  Cair num tipo padrão esconderia um nó gravado com tipo inválido. */
export function passo(tipo: string): TipoDePasso | undefined {
  return POR_TIPO.get(tipo as TipoNo);
}

export function rotuloDoTipo(tipo: string): string {
  return passo(tipo)?.rotulo ?? tipo;
}
